import { defineStore } from 'pinia';
import { ApiError, client } from '@/api';
import { ElMessage } from 'element-plus';
import { AggregatorDictResponse } from '@/open-api/models';
import { ProviderDictResponse } from '@/api/response/aggregators/provider-dict.response';

export type CascaderAggregatorChild = {
  label: string;
  value: string;
};

export type CascaderAggregator = {
  label: string;
  value: string;
  children: CascaderAggregatorChild[];
};

export type AggregatorsState = {
  loading: boolean;
  error: string | null;
  aggregators: AggregatorDictResponse[];
  providers: Set<string>;
  cascaderAggregators: CascaderAggregator[];
};

const ALL: CascaderAggregator[] = [{ label: 'All', value: '0', children: [] }];

export const useAggregators = defineStore('aggregators', {
  state: (): AggregatorsState => ({
    loading: false,
    error: null,
    aggregators: [],
    providers: new Set<string>(),
    cascaderAggregators: [...ALL],
  }),

  actions: {
    async init() {
      await this.apiV1DictAggregatorsGet();
    },

    setLoading(value: boolean) {
      this.loading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },

    async apiV1DictAggregatorsGet(): Promise<AggregatorDictResponse[]> {
      this.setError(null);
      this.setLoading(true);

      let data: AggregatorDictResponse[] = [];

      try {
        data = await client.aggregatorsApi.apiV1DictAggregatorsGet();
      } catch (err) {
        let message = 'Error during getting Aggregators dict';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      this.aggregators = data;

      this.providers = data.reduce((acc: Set<string>, item: AggregatorDictResponse) => {
        item.providers?.map((p) => p.title).forEach(acc.add, acc);

        return acc;
      }, new Set());

      this.cascaderAggregators = [...ALL].concat(
        data.map(
          (agg: AggregatorDictResponse): CascaderAggregator => ({
            label: agg.title,
            value: agg.id,
            children: (agg.providers || []).map(
              (prov: ProviderDictResponse): CascaderAggregatorChild => ({
                label: prov.title,
                value: prov.id.toString(),
              }),
            ),
          }),
        ),
      );

      return data;
    },
  },
});
